import { useState, useEffect, useRef } from 'react';
import { analyzePosition, resetStockfish, prewarmStockfish, softStopEngine, type PositionAnalysis } from '@/lib/stockfish-engine';
import { cloudEvaluate } from '@/lib/cloud-eval';
import { gameStateToFen } from '@/lib/fen-parser';
import type { GameState } from '@/lib/chess-engine';
import { getOpeningMove } from '@/lib/opening-book';
import { classifyError, getBackoffDelay } from '@/lib/engine-errors';
// Quick material evaluation for instant feedback
function getMaterialEvaluation(fen: string): number | null {
  try {
    const pieceValues = { 'p': -1, 'n': -3, 'b': -3, 'r': -5, 'q': -9, 'k': 0,
                         'P': 1, 'N': 3, 'B': 3, 'R': 5, 'Q': 9, 'K': 0 };
    
    const position = fen.split(' ')[0];
    let material = 0;
    
    for (const char of position) {
      if (char in pieceValues) {
        material += pieceValues[char as keyof typeof pieceValues];
      }
    }
    
    return material * 100; // Convert to centipawns
  } catch {
    return null;
  }
}

// Simple in-memory analysis cache (5 min TTL)
const ANALYSIS_CACHE_TTL_MS = 5 * 60 * 1000;
const analysisCache = new Map<string, { ts: number; data: PositionAnalysis }>();

function getCachedAnalysis(key: string): PositionAnalysis | null {
  const entry = analysisCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > ANALYSIS_CACHE_TTL_MS) {
    analysisCache.delete(key);
    return null;
  }
  return entry.data;
}
function setCachedAnalysis(key: string, data: PositionAnalysis) {
  analysisCache.set(key, { ts: Date.now(), data });
}

// Convert [row,col] -> UCI square (e2e4)
function toUciMove(from: [number, number], to: [number, number]): string {
  const file = (c: number) => String.fromCharCode(97 + c);
  const rank = (r: number) => String(8 - r);
  return `${file(from[1])}${rank(from[0])}${file(to[1])}${rank(to[0])}`;
}

export interface LiveAnalysisState {
  analysis: PositionAnalysis | null;
  isAnalyzing: boolean;
  error: string | null;
  hasAttemptedAnalysis: boolean;
  currentDepth?: number;
}

export function useLiveEngineAnalysis(gameState: GameState, enabled: boolean = true, moveIndex?: number) {
  const [state, setState] = useState<LiveAnalysisState>({
    analysis: null,
    isAnalyzing: false,
    error: null,
    hasAttemptedAnalysis: false,
    currentDepth: 0,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const currentPositionRef = useRef<string>('');
  const engineResetRef = useRef<boolean>(false);
  const analysisPromisesRef = useRef<Map<string, Promise<PositionAnalysis | null>>>(new Map());
  const consecutiveFailuresRef = useRef<number>(0);

  // Prewarm engine on mount for faster first analysis
  useEffect(() => {
    if (enabled) {
      prewarmStockfish().catch(() => {});
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setState(prev => ({ ...prev, isAnalyzing: false }));
      return;
    }

    const analyzeCurrentPosition = async () => {
      const fen = gameStateToFen(gameState);
      const positionKey = `${fen}-${moveIndex || 0}`;

      // Serve cached analysis instantly if available
      const cached = getCachedAnalysis(positionKey);
      if (cached) {
        setState({ 
          analysis: cached, 
          isAnalyzing: false, 
          error: null, 
          hasAttemptedAnalysis: true,
          currentDepth: cached.depth,
        });
        currentPositionRef.current = positionKey;
        return;
      }

      // Instant material evaluation for immediate feedback
      const materialEval = getMaterialEvaluation(fen);
      
      // Instant opening-book suggestion (if any)
      const book = getOpeningMove(fen, 1.0);
      const openingBookMove = book ? toUciMove(book.from as [number, number], book.to as [number, number]) : undefined;
      
      // Progressive optimistic update: show material + opening book instantly
      if (materialEval !== null || openingBookMove) {
        setState({
          analysis: {
            evaluation: materialEval ?? 0,
            bestMove: openingBookMove,
            isMate: false,
            depth: 0,
            variations: openingBookMove ? [{
              move: openingBookMove,
              evaluation: materialEval ?? 0,
              isMate: false,
              pv: [openingBookMove],
              depth: 0
            }] : []
          },
          isAnalyzing: true, // Keep analyzing for full results
          error: null,
          hasAttemptedAnalysis: true,
          currentDepth: 0,
        });
      } else {
        // No instant feedback - just show analyzing
        setState(prev => ({
          ...prev,
          isAnalyzing: true,
          error: null,
          hasAttemptedAnalysis: true,
          currentDepth: 0,
        }));
      }

      // Coalesce by position
      const existingPromise = analysisPromisesRef.current.get(positionKey);
      if (existingPromise) {
        try {
          const analysis = await existingPromise;
          if (analysis && currentPositionRef.current === positionKey) {
            setCachedAnalysis(positionKey, analysis);
            setState({ 
              analysis, 
              isAnalyzing: false, 
              error: null, 
              hasAttemptedAnalysis: true,
              currentDepth: analysis.depth,
            });
          }
        } catch {}
        return;
      }

      // Cancel previous analysis
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Preempt any in-flight engine search
      try { softStopEngine(); } catch {}

      currentPositionRef.current = positionKey;

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const analysisPromise = (async (): Promise<PositionAnalysis | null> => {
        // Compute adaptive settings once per attempt
        const currentMoveIndex = moveIndex || 0;
        const adaptiveDepth = currentMoveIndex <= 5 ? 8 : currentMoveIndex <= 10 ? 10 : 12;
        const primaryOptions = {
          movetime: currentMoveIndex <= 5 ? 500 : 800,
          multiPV: currentMoveIndex <= 5 ? 1 : 3,
          depth: adaptiveDepth,
        } as const;

        try {
          // Primary attempt
          const result = await analyzePosition(fen, primaryOptions, controller.signal);
          consecutiveFailuresRef.current = 0; // Success
          return result;
        } catch (error) {
          if (!controller.signal.aborted) {
            const engineError = classifyError(error);
            console.warn('[Live Analysis] Engine failed:', {
              type: engineError.name,
              message: engineError.userMessage,
              moveIndex,
              fen: fen.substring(0, 50),
              consecutiveFailures: consecutiveFailuresRef.current,
            });

            // Single automatic retry with MultiPV=1 (faster, more reliable) if error is retryable
            if (engineError.canRetry) {
              try {
                const backoffDelay = getBackoffDelay(0, 500);
                await new Promise(resolve => setTimeout(resolve, backoffDelay));
                
                const retryResult = await analyzePosition(
                  fen,
                  { movetime: (primaryOptions.movetime ?? 600) + 300, multiPV: 1, depth: adaptiveDepth },
                  controller.signal
                );
                consecutiveFailuresRef.current = 0;
                return retryResult;
              } catch (retryError) {
                consecutiveFailuresRef.current++;
                // Fallback to material evaluation
                const fallbackEval = materialEval ?? getMaterialEvaluation(fen) ?? 0;

                // Reset engine after 3 consecutive failures
                if (consecutiveFailuresRef.current >= 3 && !engineResetRef.current) {
                  console.warn('[Live Analysis] 3 consecutive failures, resetting engine...');
                  analysisCache.clear();
                  engineResetRef.current = true;
                  consecutiveFailuresRef.current = 0;
                  setTimeout(() => {
                    try { resetStockfish(); } catch (resetError) { console.warn('[Live Analysis] Engine reset failed:', resetError); }
                    engineResetRef.current = false;
                  }, 500);
                }

                // Return previous analysis if available, otherwise minimal fallback
                const prevAnalysis = analysisCache.get(positionKey)?.data;
                if (prevAnalysis) return prevAnalysis;

                return {
                  evaluation: fallbackEval,
                  bestMove: openingBookMove,
                  isMate: false,
                  depth: 0,
                  variations: [],
                };
              }
            }
          }
          throw error as Error;
        }
      })();

      analysisPromisesRef.current.set(positionKey, analysisPromise);

      try {
        const analysis = await analysisPromise;
        if (!controller.signal.aborted && currentPositionRef.current === positionKey && analysis) {
          setCachedAnalysis(positionKey, analysis);
          setState({ 
            analysis, 
            isAnalyzing: false, 
            error: null, 
            hasAttemptedAnalysis: true,
            currentDepth: analysis.depth,
          });
          engineResetRef.current = false;
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          const engineError = classifyError(error);
          console.error('[Live Analysis] Fatal error:', {
            type: engineError.name,
            userMessage: engineError.userMessage,
            recoveryAction: engineError.recoveryAction,
            originalError: error
          });
          
          setState(prev => ({
            analysis: prev.analysis,
            isAnalyzing: false,
            error: `${engineError.userMessage}. ${engineError.recoveryAction}`,
            hasAttemptedAnalysis: true,
            currentDepth: prev.currentDepth,
          }));
        }
      } finally {
        analysisPromisesRef.current.delete(positionKey);
      }
    };

    // No debounce for opening moves (they're fast), small debounce for later positions
    const debounce = (moveIndex || 0) <= 5 ? 0 : 30;
    const timeoutId = setTimeout(analyzeCurrentPosition, debounce);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Ensure UI doesn't stay stuck in thinking state on abort
      setState(prev => ({ ...prev, isAnalyzing: false }));
    };
  }, [gameState, enabled, moveIndex]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort(); // This will now properly stop the Stockfish search
      }
    };
  }, []);

  return state;
}